export interface CrestBrowserLaunchConfig {
  readonly supabaseUrl: string;
  readonly supabasePublishableKey: string;
  readonly tenantId: string;
  readonly applicationName: string;
  readonly publicCompanyName: string;
  readonly publicLogoUrl: string | null;
  readonly publicHeroImageUrl: string | null;
  readonly publicBrandColor: string;
  readonly publicBrandStrongColor: string;
  readonly publicAttentionColor: string;
}

type LaunchEnvironment = Readonly<Record<string, string | boolean | undefined>>;

const tenantIdPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function requiredValue(environment: LaunchEnvironment, name: string): string {
  const value = environment[name];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`CREST browser launch configuration requires ${name}.`);
  }
  return value.trim();
}

function isLoopbackHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
}

function validateSupabaseUrl(value: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error('VITE_CREST_SUPABASE_URL must be a valid absolute HTTPS URL.');
  }
  const allowsHttpLoopback = url.protocol === 'http:' && isLoopbackHost(url.hostname);
  if ((url.protocol !== 'https:' && !allowsHttpLoopback) || url.username !== '' || url.password !== '' ||
      url.pathname !== '/' || url.search !== '' || url.hash !== '') {
    throw new Error('VITE_CREST_SUPABASE_URL must be HTTPS, except explicit local loopback development.');
  }
  return url.toString().replace(/\/$/, '');
}

function validatePublishableKey(value: string): string {
  if (/^(?:sb_secret_|service_role|secret)/i.test(value) || /service[_-]?role/i.test(value)) {
    throw new Error('VITE_CREST_SUPABASE_PUBLISHABLE_KEY must not contain a secret or service-role key.');
  }
  if (!/^sb_publishable_[A-Za-z0-9_-]+$/.test(value)) {
    throw new Error('VITE_CREST_SUPABASE_PUBLISHABLE_KEY must be a Supabase publishable key.');
  }
  return value;
}

function optionalApplicationName(environment: LaunchEnvironment): string {
  const value = environment.VITE_CREST_APPLICATION_NAME;
  if (value === undefined || value === false) return 'CREST';
  if (value === true || value.trim() === '' || value.trim().length > 80 || /[\u0000-\u001f\u007f]/.test(value)) {
    throw new Error('VITE_CREST_APPLICATION_NAME must be a 1-to-80-character display name.');
  }
  return value.trim();
}

function optionalDisplayName(
  environment: LaunchEnvironment,
  name: string,
  fallback: string,
): string {
  const value = environment[name];
  if (value === undefined || value === false) return fallback;
  if (value === true || value.trim() === '' || value.trim().length > 100 || /[\u0000-\u001f\u007f]/.test(value)) {
    throw new Error(`${name} must be a 1-to-100-character display name.`);
  }
  return value.trim();
}

function optionalPublicAssetUrl(environment: LaunchEnvironment, name: string): string | null {
  const value = environment[name];
  if (value === undefined || value === false) return null;
  if (value === true || value.trim() === '') throw new Error(`${name} must be a public asset URL.`);
  const trimmed = value.trim();
  if (trimmed.startsWith('/') && !trimmed.startsWith('//') && !trimmed.includes('\\')) return trimmed;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    throw new Error(`${name} must be a root-relative path or absolute HTTPS URL.`);
  }
  if (url.protocol !== 'https:' || url.username !== '' || url.password !== '') {
    throw new Error(`${name} must be a root-relative path or absolute HTTPS URL.`);
  }
  return url.toString();
}

function optionalPublicColor(environment: LaunchEnvironment, name: string, fallback: string): string {
  const value = environment[name];
  if (value === undefined || value === false) return fallback;
  if (value === true || !/^#[0-9a-f]{6}$/i.test(value.trim())) {
    throw new Error(`${name} must be a six-digit hexadecimal color.`);
  }
  return value.trim().toLowerCase();
}

export function readCrestBrowserLaunchConfig(environment: LaunchEnvironment): CrestBrowserLaunchConfig {
  const supabaseUrl = validateSupabaseUrl(requiredValue(environment, 'VITE_CREST_SUPABASE_URL'));
  const supabasePublishableKey = validatePublishableKey(
    requiredValue(environment, 'VITE_CREST_SUPABASE_PUBLISHABLE_KEY'),
  );
  const tenantId = requiredValue(environment, 'VITE_CREST_TENANT_ID');
  if (!tenantIdPattern.test(tenantId)) {
    throw new Error('VITE_CREST_TENANT_ID must be a UUID.');
  }
  const applicationName = optionalApplicationName(environment);
  return {
    supabaseUrl,
    supabasePublishableKey,
    tenantId: tenantId.toLowerCase(),
    applicationName,
    publicCompanyName: optionalDisplayName(
      environment,
      'VITE_CREST_PUBLIC_COMPANY_NAME',
      applicationName,
    ),
    publicLogoUrl: optionalPublicAssetUrl(environment, 'VITE_CREST_PUBLIC_LOGO_URL'),
    publicHeroImageUrl: optionalPublicAssetUrl(environment, 'VITE_CREST_PUBLIC_HERO_IMAGE_URL'),
    publicBrandColor: optionalPublicColor(environment, 'VITE_CREST_PUBLIC_BRAND_COLOR', '#475569'),
    publicBrandStrongColor: optionalPublicColor(
      environment,
      'VITE_CREST_PUBLIC_BRAND_STRONG_COLOR',
      '#1f2937',
    ),
    publicAttentionColor: optionalPublicColor(environment, 'VITE_CREST_PUBLIC_ATTENTION_COLOR', '#64748b'),
  };
}

export function loadCrestBrowserLaunchConfig(): CrestBrowserLaunchConfig {
  return readCrestBrowserLaunchConfig(import.meta.env);
}
